/**
 * React Query hooks for the Escalations screen. Mutations invalidate
 * both the list and the stats query so the badges + table stay in sync
 * after assign/resolve/dismiss without a manual refetch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EscalationListParams,
  EscalationUpdateInput,
  fetchEscalationStats,
  fetchEscalations,
  updateEscalation
} from '@/api/escalations';

export function useEscalations(params: EscalationListParams) {
  return useQuery({
    queryKey: ['escalations', params],
    queryFn: () => fetchEscalations(params),
    refetchInterval: 60_000,
    placeholderData: prev => prev
  });
}

export function useEscalationStats() {
  return useQuery({
    queryKey: ['escalationStats'],
    queryFn: fetchEscalationStats,
    refetchInterval: 60_000
  });
}

export function useUpdateEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EscalationUpdateInput) => updateEscalation(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['escalations'] });
      qc.invalidateQueries({ queryKey: ['escalationStats'] });
    }
  });
}
